<script setup lang="ts">
import DisplayPanel from './DisplayPanel.vue';
import Keypad from './Keypad.vue';
import { useCalculator } from '../composables/useCalculator';
import { useKeyboard } from '../composables/useKeyboard';
import type { KeyDef } from '../types';

const calc = useCalculator(true);

/** Win11 科学模式 5×6 */
const keys: KeyDef[] = [
  { label: 'x²', action: 'square', class: 'fn' },
  { label: 'π', value: 'pi', class: 'fn' },
  { label: 'e', value: 'e', class: 'fn' },
  { label: 'CE', action: 'ce', class: 'fn' },
  { label: '', action: 'back', class: 'fn', icon: 'backspace' },
  { label: '²√x', action: 'sqrt', class: 'fn' },
  { label: '(', value: '(', class: 'fn' },
  { label: ')', value: ')', class: 'fn' },
  { label: 'n!', action: 'factorial', class: 'fn' },
  { label: '÷', value: '÷', class: 'op' },
  { label: 'xʸ', value: '^', class: 'fn' },
  { label: '7', value: '7', class: 'num' },
  { label: '8', value: '8', class: 'num' },
  { label: '9', value: '9', class: 'num' },
  { label: '×', value: '×', class: 'op' },
  { label: '10ˣ', value: '10^', class: 'fn' },
  { label: '4', value: '4', class: 'num' },
  { label: '5', value: '5', class: 'num' },
  { label: '6', value: '6', class: 'num' },
  { label: '−', value: '-', class: 'op' },
  { label: 'log', action: 'log10', class: 'fn' },
  { label: '1', value: '1', class: 'num' },
  { label: '2', value: '2', class: 'num' },
  { label: '3', value: '3', class: 'num' },
  { label: '+', value: '+', class: 'op' },
  { label: 'ln', action: 'ln', class: 'fn' },
  { label: 'mod', value: 'mod', class: 'fn' },
  { label: '0', value: '0', class: 'num' },
  { label: '.', value: '.', class: 'num' },
  { label: '=', action: 'equals', class: 'equals' },
];

function onKey(k: KeyDef) {
  if (k.action === 'clear') calc.clearAll();
  else if (k.action === 'ce') calc.clearEntry();
  else if (k.action === 'back') calc.backspace();
  else if (k.action === 'equals') calc.calculate();
  else if (k.action === 'sqrt') calc.applySqrt();
  else if (k.action === 'square') calc.applySquare();
  else if (k.action === 'factorial') calc.applyFactorial();
  else if (k.action === 'log10') calc.pressLog10();
  else if (k.action === 'ln') calc.pressLn();
  else if (k.value) calc.append(k.value);
}

useKeyboard((e) => {
  const k = e.key;
  if (k >= '0' && k <= '9') {
    calc.append(k);
    return true;
  }
  if (k === '.') {
    calc.append('.');
    return true;
  }
  if (k === '+' || k === '-') {
    calc.append(k);
    return true;
  }
  if (k === '*') {
    calc.append('×');
    return true;
  }
  if (k === '/') {
    calc.append('÷');
    return true;
  }
  if (k === '(' || k === ')') {
    calc.append(k);
    return true;
  }
  if (k === 'Enter' || k === '=') {
    calc.calculate();
    return true;
  }
  if (k === 'Backspace') {
    calc.backspace();
    return true;
  }
  if (k === 'Escape') {
    calc.clearAll();
    return true;
  }
  if (k === 'Delete') {
    calc.clearEntry();
    return true;
  }
  return false;
});
</script>

<template>
  <div class="calc-view sci">
    <DisplayPanel
      :formula="calc.formulaLine.value"
      :result="calc.displayMain.value"
      :memory="calc.memory.value"
      :error="calc.error.value"
      xlarge
    />
    <Keypad :keys="keys" :columns="5" stretch class="sci-keypad" @press="onKey" />
  </div>
</template>

<style scoped>
.sci :deep(.win-key.num) {
  font-weight: 600;
  font-size: 16px;
}
.sci-keypad :deep(.win-key.fn) {
  font-size: 13px;
}
</style>
